/**
 * 通用树操作工具函数
 *
 * 提供对树形结构数据的常用操作：扁平化、查找、过滤、映射、构建等。
 * 适用于菜单树、分类树、组织树、权限树等各种树形场景。
 *
 */

/** 默认子节点字段名 */
const DEFAULT_CHILDREN_KEY = 'children';

/**
 * 将树形结构扁平化为一维数组
 *
 * @param tree - 树节点数组
 * @param childrenKey - 子节点字段名，默认 'children'
 * @returns 扁平化后的节点数组（不含 children 属性）
 *
 * @example
 * ```ts
 * const tree = [
 *   { id: 1, name: 'A', children: [{ id: 2, name: 'B' }] },
 *   { id: 3, name: 'C' },
 * ];
 * flattenTree(tree);
 * // => [{ id: 1, name: 'A' }, { id: 2, name: 'B' }, { id: 3, name: 'C' }]
 * ```
 */
export function flattenTree<T extends Record<string, unknown>>(
  tree: T[],
  childrenKey: string = DEFAULT_CHILDREN_KEY,
): T[] {
  const result: T[] = [];

  function walk(nodes: T[]) {
    for (const node of nodes) {
      const { [childrenKey]: children, ...rest } = node;
      result.push(rest as T);
      if (Array.isArray(children) && children.length > 0) {
        walk(children as T[]);
      }
    }
  }

  walk(tree);
  return result;
}

/**
 * 在树中查找满足条件的第一个节点
 *
 * @param tree - 树节点数组
 * @param predicate - 匹配函数
 * @param childrenKey - 子节点字段名，默认 'children'
 * @returns 匹配的节点，未找到返回 null
 *
 * @example
 * ```ts
 * const node = findNodeInTree(tree, (n) => n.id === 5);
 * ```
 */
export function findNodeInTree<T extends Record<string, unknown>>(
  tree: T[],
  predicate: (node: T) => boolean,
  childrenKey: string = DEFAULT_CHILDREN_KEY,
): T | null {
  for (const node of tree) {
    if (predicate(node)) return node;
    const children = node[childrenKey];
    if (Array.isArray(children) && children.length > 0) {
      const found = findNodeInTree(children as T[], predicate, childrenKey);
      if (found) return found;
    }
  }
  return null;
}

/**
 * 在树中查找目标节点的父节点
 *
 * @param tree - 树节点数组
 * @param predicate - 目标节点匹配函数
 * @param childrenKey - 子节点字段名，默认 'children'
 * @returns 父节点，未找到或目标在根层级时返回 null
 *
 * @example
 * ```ts
 * const parent = findParentNode(tree, (n) => n.id === 5);
 * ```
 */
export function findParentNode<T extends Record<string, unknown>>(
  tree: T[],
  predicate: (node: T) => boolean,
  childrenKey: string = DEFAULT_CHILDREN_KEY,
): T | null {
  for (const node of tree) {
    const children = node[childrenKey];
    if (Array.isArray(children)) {
      for (const child of children as T[]) {
        if (predicate(child)) return node;
      }
      const found = findParentNode(children as T[], predicate, childrenKey);
      if (found) return found;
    }
  }
  return null;
}

/**
 * 过滤树节点，保留匹配节点及其祖先路径
 *
 * 如果子节点匹配，其所有祖先节点也会被保留（即使祖先节点本身不匹配）。
 *
 * @param tree - 树节点数组
 * @param predicate - 匹配函数
 * @param childrenKey - 子节点字段名，默认 'children'
 * @returns 过滤后的新树
 *
 * @example
 * ```ts
 * const filtered = filterTree(menuTree, (n) => n.visible !== false);
 * ```
 */
export function filterTree<T extends Record<string, unknown>>(
  tree: T[],
  predicate: (node: T) => boolean,
  childrenKey: string = DEFAULT_CHILDREN_KEY,
): T[] {
  const result: T[] = [];

  for (const node of tree) {
    const children = node[childrenKey];
    const filteredChildren = Array.isArray(children)
      ? filterTree(children as T[], predicate, childrenKey)
      : [];

    // 保留：节点本身匹配 或 有匹配的子节点
    if (predicate(node) || filteredChildren.length > 0) {
      result.push({
        ...node,
        [childrenKey]:
          filteredChildren.length > 0 ? filteredChildren : undefined,
      });
    }
  }

  return result;
}

/**
 * 从扁平列表构建树形结构
 *
 * @param list - 扁平节点列表
 * @param options - 配置选项
 * @returns 树形结构数组
 *
 * @example
 * ```ts
 * const list = [
 *   { id: 1, parentId: 0, name: 'Root' },
 *   { id: 2, parentId: 1, name: 'Child 1' },
 *   { id: 3, parentId: 1, name: 'Child 2' },
 * ];
 * const tree = buildTreeFromFlat(list, { rootParentId: 0 });
 * ```
 */
export function buildTreeFromFlat<T extends Record<string, unknown>>(
  list: T[],
  options?: {
    /** ID 字段名，默认 'id' */
    idKey?: string;
    /** 父 ID 字段名，默认 'parentId' */
    parentIdKey?: string;
    /** 子节点字段名，默认 'children' */
    childrenKey?: string;
    /** 根节点的 parentId 值，默认 null/undefined/0/'' */
    rootParentId?: string | number | null;
  },
): T[] {
  const {
    idKey = 'id',
    parentIdKey = 'parentId',
    childrenKey = DEFAULT_CHILDREN_KEY,
    rootParentId,
  } = options ?? {};

  const nodeMap = new Map<string | number, T & { [key: string]: unknown }>();
  const roots: T[] = [];

  // 第一遍：建立 id -> node 映射
  for (const item of list) {
    const id = item[idKey] as string | number;
    nodeMap.set(id, { ...item, [childrenKey]: [] });
  }

  // 第二遍：挂载子节点
  for (const item of list) {
    const id = item[idKey] as string | number;
    const parentId = item[parentIdKey] as string | number | null | undefined;
    const node = nodeMap.get(id);
    if (!node) {
      continue;
    }

    const isRoot =
      rootParentId !== undefined
        ? parentId === rootParentId
        : parentId === null ||
          parentId === undefined ||
          parentId === 0 ||
          parentId === '';

    if (isRoot) {
      roots.push(node as T);
    } else if (parentId !== null && parentId !== undefined) {
      const parent = nodeMap.get(parentId);
      if (parent) {
        (parent[childrenKey] as T[]).push(node as T);
      } else {
        // 父节点不存在时当作根节点
        roots.push(node as T);
      }
    }
  }

  // 清理空 children 数组
  function cleanEmptyChildren(nodes: T[]) {
    for (const node of nodes) {
      const children = (node as Record<string, unknown>)[childrenKey] as T[];
      if (Array.isArray(children)) {
        if (children.length === 0) {
          delete (node as Record<string, unknown>)[childrenKey];
        } else {
          cleanEmptyChildren(children);
        }
      }
    }
  }

  cleanEmptyChildren(roots);
  return roots;
}

/**
 * 映射树的每个节点，生成新树
 *
 * @param tree - 原始树
 * @param mapper - 映射函数
 * @param childrenKey - 子节点字段名，默认 'children'
 * @returns 映射后的新树
 *
 * @example
 * ```ts
 * const options = mapTree(categories, (node) => ({
 *   label: node.name,
 *   value: node.id,
 * }));
 * ```
 */
export function mapTree<
  T extends Record<string, unknown>,
  R extends Record<string, unknown>,
>(
  tree: T[],
  mapper: (node: T, depth: number) => R,
  childrenKey: string = DEFAULT_CHILDREN_KEY,
): R[] {
  function walk(nodes: T[], depth: number): R[] {
    return nodes.map((node) => {
      const mapped = mapper(node, depth);
      const children = node[childrenKey];
      if (Array.isArray(children) && children.length > 0) {
        return { ...mapped, [childrenKey]: walk(children as T[], depth + 1) };
      }
      return mapped;
    });
  }

  return walk(tree, 0);
}

/**
 * 判断一个节点是否是另一个节点的后代
 *
 * 单次遍历实现：找到祖先节点后立即在其子树中搜索目标，避免两次全树遍历。
 *
 * @param tree - 树节点数组
 * @param ancestorId - 祖先节点 ID
 * @param targetId - 目标节点 ID
 * @param idKey - ID 字段名，默认 'id'
 * @param childrenKey - 子节点字段名，默认 'children'
 * @returns 是否为后代关系
 *
 * @example
 * ```ts
 * isDescendant(tree, 1, 5) // true: 节点 5 是节点 1 的后代
 * ```
 */
export function isDescendant<T extends Record<string, unknown>>(
  tree: T[],
  ancestorId: string | number,
  targetId: string | number,
  idKey: string = 'id',
  childrenKey: string = DEFAULT_CHILDREN_KEY,
): boolean {
  // 单次递归：遍历到祖先节点后，仅在其子树中搜索目标
  function searchInSubtree(nodes: T[]): boolean {
    for (const node of nodes) {
      if (node[idKey] === ancestorId) {
        // 找到祖先，仅在其子树中搜索目标
        const children = node[childrenKey];
        if (!Array.isArray(children)) return false;
        return (
          findNodeInTree(
            children as T[],
            (n) => n[idKey] === targetId,
            childrenKey,
          ) !== null
        );
      }
      // 继续深搜祖先节点
      const children = node[childrenKey];
      if (Array.isArray(children) && children.length > 0) {
        const found = searchInSubtree(children as T[]);
        if (found) return true;
      }
    }
    return false;
  }

  return searchInSubtree(tree);
}

/**
 * 获取从根到目标节点的路径
 *
 * @param tree - 树节点数组
 * @param predicate - 目标节点匹配函数
 * @param childrenKey - 子节点字段名，默认 'children'
 * @returns 路径数组（从根到目标），未找到返回空数组
 *
 * @example
 * ```ts
 * const path = getNodePath(tree, (n) => n.id === 5);
 * // => [rootNode, parentNode, targetNode]
 * ```
 */
export function getNodePath<T extends Record<string, unknown>>(
  tree: T[],
  predicate: (node: T) => boolean,
  childrenKey: string = DEFAULT_CHILDREN_KEY,
): T[] {
  function walk(nodes: T[], path: T[]): T[] | null {
    for (const node of nodes) {
      const currentPath = [...path, node];
      if (predicate(node)) return currentPath;
      const children = node[childrenKey];
      if (Array.isArray(children) && children.length > 0) {
        const found = walk(children as T[], currentPath);
        if (found) return found;
      }
    }
    return null;
  }

  return walk(tree, []) ?? [];
}

/**
 * 递归排序树的每一层节点
 *
 * @param tree - 树节点数组
 * @param compareFn - 比较函数，同 Array.sort
 * @param childrenKey - 子节点字段名，默认 'children'
 * @returns 排序后的新树
 *
 * @example
 * ```ts
 * const sorted = sortTree(menuTree, (a, b) => a.orderSeq - b.orderSeq);
 * ```
 */
export function sortTree<T extends Record<string, unknown>>(
  tree: T[],
  compareFn: (a: T, b: T) => number,
  childrenKey: string = DEFAULT_CHILDREN_KEY,
): T[] {
  return [...tree].sort(compareFn).map((node) => {
    const children = node[childrenKey];
    if (Array.isArray(children) && children.length > 0) {
      return {
        ...node,
        [childrenKey]: sortTree(children as T[], compareFn, childrenKey),
      };
    }
    return node;
  });
}

/**
 * 遍历树的每个节点（深度优先）
 *
 * @param tree - 树节点数组
 * @param callback - 回调函数，返回 false 可中止遍历
 * @param childrenKey - 子节点字段名，默认 'children'
 *
 * @example
 * ```ts
 * walkTree(menuTree, (node, depth) => {
 *   console.log('  '.repeat(depth) + node.name);
 * });
 * ```
 */
export function walkTree<T extends Record<string, unknown>>(
  tree: T[],
  callback: (node: T, depth: number, parent: T | null) => undefined | false,
  childrenKey: string = DEFAULT_CHILDREN_KEY,
): void {
  function walk(nodes: T[], depth: number, parent: T | null): boolean {
    for (const node of nodes) {
      const result = callback(node, depth, parent);
      if (result === false) return false;
      const children = node[childrenKey];
      if (Array.isArray(children) && children.length > 0) {
        const shouldContinue = walk(children as T[], depth + 1, node);
        if (!shouldContinue) return false;
      }
    }
    return true;
  }

  walk(tree, 0, null);
}
